import { useState, useCallback } from 'react'
import {
  AGORA_RTC_LAYOUTS,
  getLegacyPlatformIdFromSessionRouting,
  normalizeAgoraRtcLayout,
} from 'src/live'
import { 
  getAgoraToken, 
  getAgoraChatToken,
  markBookedEventLog 
} from 'src/consumer/services/featureServices'
import { generateUidForAgora } from 'src/consumer/helpers/utilFunctions'
import { allAvailableEventTypes } from 'src/consumer/constants'
import { isCommonResponseSuccessful } from 'src/mentor/helpers/utilityFunctions'
import {
  buildMentorRtmUsername,
  buildPatientRtmUsername,
} from 'src/mentor/components/MentorClinicSomeChamberAutoJoiningPage/utils/rtmUsernameBuilder'

export const useAgoraConnection = () => {
  const [agoraRtcProps, setAgoraRtcProps] = useState({})
  const [agoraRtmProps, setAgoraRtmProps] = useState({})
  const [uidObject, setUidObject] = useState({})
  
  // Chat related
  const [myChatId, setMyChatId] = useState()
  const [myChatToken, setMyChatToken] = useState()
  const [peerChatId, setPeerChatId] = useState()
  const [peerChatName, setPeerChatName] = useState()
  const [peerChatImage, setPeerChatImage] = useState()

  const initializeUidObject = useCallback(() => {
    setUidObject({
      uid: generateUidForAgora(),
      uidScreen: generateUidForAgora(),
      uidRecording: generateUidForAgora(),
    })
  }, [])

  const handleJoinRoom = useCallback(async ({
    bookedEventFinalData,
    userRole,
    username,
    userHasAllowedAudio,
    userHasAllowedVideo,
    selectedCamera,
    selectedMicrophone,
    setTokenLoading,
    setSessionStarted,
    setIsCameraOpen,
    setIsAudioOpen,
    setBookedEventFinalData,
    selectedPlatform,
    patientInfo = null,
    userProfileData = null,
    mentorProfileData = null,
  }) => {
    console.log('=== useAgoraConnection.handleJoinRoom CALLED ===', {
      hasBookedEventFinalData: !!bookedEventFinalData,
      hasSelectedPlatform: !!selectedPlatform,
      hasUserRole: !!userRole,
      bookedEventFinalData,
      selectedPlatform,
      userRole,
    })
    
    // Defensive checks
    if (!bookedEventFinalData) {
      console.error('❌ [useAgoraConnection.handleJoinRoom] bookedEventFinalData is required')
      return { success: false, error: 'bookedEventFinalData is required' }
    }

    const resolvedSelectedPlatform =
      selectedPlatform ||
      (bookedEventFinalData
        ? {
            id: getLegacyPlatformIdFromSessionRouting(
              bookedEventFinalData?.sessionRoutingDecision,
              bookedEventFinalData?.legacyPlatformId || bookedEventFinalData?.meetingSrc,
            ),
          }
        : null)
    
    if (!resolvedSelectedPlatform?.id) {
      console.error('❌ [useAgoraConnection.handleJoinRoom] selectedPlatform is required', {
        meetingSrc: bookedEventFinalData?.meetingSrc,
        bookedEventFinalData,
        resolvedPlatformId: resolvedSelectedPlatform?.id,
      })
      return { success: false, error: 'selectedPlatform is required' }
    }
    
    if (!userRole) {
      console.error('❌ [useAgoraConnection.handleJoinRoom] userRole is required')
      return { success: false, error: 'userRole is required' }
    }
    
    console.log('✅ [useAgoraConnection.handleJoinRoom] All checks passed. Starting join process:', {
      platform: resolvedSelectedPlatform?.id,
      eventId: bookedEventFinalData?.id,
      eventType: bookedEventFinalData?.eventType,
      userRole,
      meetingSrc: bookedEventFinalData?.meetingSrc,
    })
    
    if (resolvedSelectedPlatform?.id === 'aumhum') {
      setTokenLoading(true)
      let response
      let responseScreen
      let role = 'host'
      
      if (userRole === 'mentor') {
        response = await getAgoraToken(bookedEventFinalData?.id, true)
        responseScreen = await getAgoraToken(bookedEventFinalData?.id, true)
      } else {
        if (bookedEventFinalData?.eventType === allAvailableEventTypes.webinar) {
          role = 'audience'
        }
        response = await getAgoraToken(bookedEventFinalData?.id, false)
        responseScreen = await getAgoraToken(bookedEventFinalData?.id, false)
      }
      
      if (
        response?.status === 200 &&
        isCommonResponseSuccessful(response?.data?.code) &&
        response?.data?.data?.userName &&
        response?.data?.data?.rtcToken &&
        response?.data?.data?.rtmToken &&
        responseScreen?.status === 200 &&
        isCommonResponseSuccessful(responseScreen?.data?.code) &&
        responseScreen?.data?.data?.userName &&
        responseScreen?.data?.data?.rtcToken &&
        responseScreen?.data?.data?.rtmToken
      ) {
        const responseRoutingDecision = response?.data?.data?.sessionRoutingDecision
        const resolvedVideoQuality =
          responseRoutingDecision?.videoQuality || response?.data?.data?.videoQuality || null
        if (responseRoutingDecision && typeof setBookedEventFinalData === 'function') {
          setBookedEventFinalData((previousBookedEventData) => {
            if (!previousBookedEventData) {
              return previousBookedEventData
            }

            return {
              ...previousBookedEventData,
              legacyPlatformId:
                response?.data?.data?.legacyPlatformId || previousBookedEventData?.legacyPlatformId,
              sessionMode:
                response?.data?.data?.sessionMode || previousBookedEventData?.sessionMode,
              rtcProviderResolved:
                response?.data?.data?.rtcProviderResolved ||
                previousBookedEventData?.rtcProviderResolved,
              signalingProviderResolved:
                response?.data?.data?.signalingProviderResolved ||
                previousBookedEventData?.signalingProviderResolved,
              sessionInterfaceId:
                response?.data?.data?.sessionInterfaceId ||
                previousBookedEventData?.sessionInterfaceId,
              localParticipantIdentity:
                response?.data?.data?.localParticipantIdentity ||
                previousBookedEventData?.localParticipantIdentity,
              sessionRoutingDecision: responseRoutingDecision,
            }
          })
        }

        setAgoraRtcProps({
          appId: response?.data?.data?.appId || import.meta.env.VITE_AGORA_APP_ID,
          channel: bookedEventFinalData?.id,
          uid: parseInt(response?.data?.data?.userName),
          token: response?.data?.data?.rtcToken,
          enableScreensharing: true,
          screenshareUid: responseScreen?.data?.data?.userName,
          screenshareToken: responseScreen?.data?.data?.rtcToken,
          layout: AGORA_RTC_LAYOUTS.pin,
          role,
          enableDualStream: resolvedVideoQuality?.enableDualStream === true,
          enableAudio: userHasAllowedAudio,
          enableVideo: userHasAllowedVideo,
          cameraDeviceId: selectedCamera,
          microphoneDeviceId: selectedMicrophone,
          videoQuality: resolvedVideoQuality,
        })
        
        // Build RTM username with role, name, and profilePicId
        // For patients/endUsers, use patientInfo if available
        let rtmUsername = username
        if (userRole === 'mentor') {
          rtmUsername = buildMentorRtmUsername(mentorProfileData, userProfileData)
        } else if (
          userRole !== 'panelist' &&
          userRole !== 'support' &&
          (patientInfo || userProfileData)
        ) {
          const patientInfoObj = patientInfo || { name: username }
          rtmUsername = buildPatientRtmUsername(patientInfoObj, userProfileData, username)
        }
        
        setAgoraRtmProps({
          username: rtmUsername,
          displayUsername: true,
          token: response?.data?.data?.rtmToken,
          showPopUpBeforeRemoteMute: false,
          isThisUserAllowedToMuteOthers: userRole === 'mentor' || userRole === 'panelist',
        })
        
        setIsCameraOpen(userHasAllowedVideo)
        setIsAudioOpen(userHasAllowedAudio)
        setTokenLoading(false)
      } else {
        // Token request failed - log error but still allow session to start
        console.error('Failed to get Agora tokens:', {
          responseStatus: response?.status,
          responseCode: response?.data?.code,
          responseScreenStatus: responseScreen?.status,
          responseScreenCode: responseScreen?.data?.code,
        })
        setTokenLoading(false)
      }
      
      // Always try to get chat token (non-blocking)
      try {
        const chatTokenResponse = await getAgoraChatToken()
        if (
          chatTokenResponse?.status === 200 &&
          isCommonResponseSuccessful(chatTokenResponse?.data?.code) &&
          chatTokenResponse?.data?.data?.chatToken
        ) {
          setMyChatToken(chatTokenResponse?.data?.data?.chatToken)
        }
      } catch (error) {
        console.error('Failed to get chat token:', error)
        // Non-blocking - continue even if chat token fails
      }
      
      // Always set session started, even if token requests failed
      // This ensures the button works and user can proceed
      setSessionStarted(true)
      console.log('[handleJoinRoom] Session started for aumhum platform')
      return { success: true, platform: resolvedSelectedPlatform?.id }
    } else {
      // For non-aumhum platforms (offline, phone, etc.)
      setSessionStarted(true)
      console.log(
        '[handleJoinRoom] Session started for non-aumhum platform:',
        resolvedSelectedPlatform?.id,
      )
      return { success: true, platform: resolvedSelectedPlatform?.id }
    }
  }, [])

  const changeLayoutFunction = useCallback(() => {
    setAgoraRtcProps((prev) => ({
      ...prev,
      layout:
        normalizeAgoraRtcLayout(prev.layout) === AGORA_RTC_LAYOUTS.grid
          ? AGORA_RTC_LAYOUTS.pin
          : AGORA_RTC_LAYOUTS.grid,
    }))
  }, [])

  const joinAgoraCallFunction = useCallback((
    bookedEventFinalData,
    userRole,
    setJoinAgoraCall,
    isCameraOpen,
    isAudioOpen
  ) => {
    markBookedEventLog(bookedEventFinalData?.id, 'JOIN_AGORA_CALL', userRole, {
      camera: isCameraOpen,
      mic: isAudioOpen,
    })
    setJoinAgoraCall(true)
  }, [])

  return {
    // State
    agoraRtcProps,
    setAgoraRtcProps,
    agoraRtmProps,
    setAgoraRtmProps,
    uidObject,
    setUidObject,
    
    // Chat
    myChatId,
    setMyChatId,
    myChatToken,
    setMyChatToken,
    peerChatId,
    setPeerChatId,
    peerChatName,
    setPeerChatName,
    peerChatImage,
    setPeerChatImage,
    
    // Methods
    initializeUidObject,
    handleJoinRoom,
    changeLayoutFunction,
    joinAgoraCallFunction,
  }
}
